import { maybe, rd } from '@passionware/monads';
import type { AppScope } from 'v5/core/app-scope/appScope.types';
import { FeatureFlag, Resource } from '__types__';
import { CapabilityEnum } from 'dtos/constants/capability.enum';

import {
  convertImageFileWithCloudinary,
  getAvatarImageURL,
  IMAGE_QUALITY_OPTIONS
} from 'lib/media/imageHelper';
import { useRestrictedAccess } from 'lib/restrictedAccess';
import { ArrowUpRight, CalendarSearch, Stamp } from 'lucide-react';
import PropTypes from 'prop-types';
import { type ReactNode, useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useFileInput } from 'v5/platform/react/useFileInput';

import { openModal } from 'store/v1/ui/ui.actions.js';
import { myServices } from 'v5/services/services.connected.app';
import { useIsResourcePaneV6 } from 'v5/features/resources/useIsResourcePaneV6';
import { ResourceEditorVersionSwitch } from 'v5/features/resources/ResourceEditorVersionSwitch';
import { ResourceAvatarSilhouette } from 'v5/patterns/resource/ResourceAvatarSilhouette';

import {
  ResourceOptions,
  ResourceTypeSelectorDisplay
} from 'v1/components/shared';
import { renderName } from 'v1/helpers/contactHelpers';
import { Link } from 'react-router-dom';
import { contactDetailPathInScope } from 'v5/core/app-scope/contactDetailPathInScope';
import { parseInitials } from 'v1/helpers/misc';
import { Button } from 'v5/design-system/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import {
  SimpleTooltip,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'v5/design-system/Tooltip';
import { EntityHeader } from 'v5/features/core/entity-header/EntityHeader';
import { EntityHeaderActions } from 'v5/features/core/entity-header/EntityHeaderActions';
import { EntityHeaderBackgroundImage } from 'v5/features/core/entity-header/EntityHeaderBackgroundImage';
import { EntityHeaderImage } from 'v5/features/core/entity-header/EntityHeaderImage';
import {
  EntityHeaderSubtitle,
  EntityHeaderSubtitleButton
} from 'v5/features/core/entity-header/EntityHeaderSubtitle';
import { EntityHeaderTitle } from 'v5/features/core/entity-header/EntityHeaderTitle';
import { HamburgerMenu } from 'v5/features/core/HamburgerMenu';
import { DockableHeader } from 'v5/patterns/sidebar/DockableLayout';
import { myToastService } from 'v5/services/front/ToastService/ToastService.connected';
import { SingleImageLightbox } from 'v5/patterns/forms/fields/image-upload/SingleImageLightbox';
import { ResourceGroupsEditor } from 'v5/features/resources/form/ResourceGroupsEditor';
import { ResourceEffect } from 'v4/entities/resource/resource.effect.types';
import { EntityListRowErrorBoundary } from 'v5/patterns/entity-list/EntityListRowErrorBoundary';

const ResourcePaneHeader = ({
  resource = {} as Resource,
  hideTabs,
  hideOptions,
  hideFullLink,
  onAction,
  onEditName,
  notice,
  appScope
}: {
  resource?: Resource;
  hideTabs?: boolean;
  hideOptions?: boolean;
  hideFullLink?: boolean;
  onAction: (action: ResourceEffect) => Promise<void>;
  /** Double-clicking the name: edit it in the v6 form below. */
  onEditName?: () => void;
  /** A line below the groups, e.g. that the stored data failed validation. */
  notice?: ReactNode;
  /** Browsing scope of the opener, for "View Profile". */
  appScope: AppScope;
}) => {
  const dispatch = useDispatch();
  const { t: tRow } = useTranslation('patterns', {
    keyPrefix: 'EntityListRow'
  });
  const [codeCopyState, setCodeCopyState] = useState<boolean | undefined>();
  const [imageOpen, setImageOpen] = useState(false);
  const handleChange = (field, value) => {
    onAction({ type: 'update', payload: { [field]: value } });
  };

  useEffect(() => {
    if (codeCopyState) {
      myToastService.open({
        type: 'success',
        message: 'Resource code copied'
      });
      setTimeout(() => {
        setCodeCopyState(false);
      }, 500);
    }
  }, [codeCopyState]);

  const canUpdate = useRestrictedAccess(null, CapabilityEnum.RESOURCE_UPDATE);
  const canUpdateBookings = useRestrictedAccess(
    FeatureFlag.BOOKINGS,
    CapabilityEnum.BOOKING_UPDATE
  );
  const canCreateAvailability = useRestrictedAccess(
    FeatureFlag.BOOKINGS,
    CapabilityEnum.AVAILABILITY_CREATE
  );
  const slugAppScope = myServices.appScopeService.useSlugAppScope();
  const profilePath = contactDetailPathInScope(appScope, resource.id);
  const isResourcePaneV6 = useIsResourcePaneV6(myServices);
  const menuDisabled = !canUpdate && !resource.profile_picture;

  const triggerFileInput = useFileInput({
    accept: 'image/*',
    onFileSelected: async file => {
      try {
        const result = await myServices.uploadService.uploadMedia({
          media: file,
          secure: false,
          onProgress: progress => {
            console.log('progress', progress);
          }
        });
        // Update the resource's profile picture
        handleChange('profile_picture', result.accessUrl);
      } catch (error) {
        console.error('Upload error:', error);
        myToastService.open({
          type: 'error',
          message: 'Failed to upload profile picture'
        });
      }
    }
  });

  return (
    <>
      <div className="tailwind-root contents">
        <DockableHeader>
          <div className="p-4 empty:hidden">
            <HamburgerMenu />
          </div>
        </DockableHeader>
      </div>
      <div className="tailwind-root contents">
        <EntityHeader
          titleContent={
            <EntityHeaderTitle
              title={rd.of(renderName(resource))}
              pronounsOptionId={resource.pronouns_option_id ?? null}
              resourceTypeId={resource.resource_type_id ?? null}
              placeholder={isResourcePaneV6 ? 'Unnamed resource' : undefined}
              onDoubleClick={
                isResourcePaneV6 && canUpdate ? onEditName : undefined
              }
            />
          }
          subtitleContent={
            <EntityHeaderSubtitle
              subtitle={rd.of(
                <div className="flex items-center gap-1">
                  <Tooltip>
                    <CopyToClipboard
                      text={resource.id}
                      onCopy={() => setCodeCopyState(true)}
                    >
                      <TooltipTrigger asChild>
                        <EntityHeaderSubtitleButton>
                          {resource.id}
                        </EntityHeaderSubtitleButton>
                      </TooltipTrigger>
                    </CopyToClipboard>
                    <TooltipContent side="bottom" align="start">
                      Click to copy code
                    </TooltipContent>
                  </Tooltip>
                  <span>·</span>

                  <Tooltip open={canUpdate ? undefined : false}>
                    <ResourceTypeSelectorDisplay
                      size="S"
                      resourceTypeId={resource.resource_type_id}
                      onChange={value =>
                        handleChange('resource_type_id', value)
                      }
                      // @ts-expect-error migrate to ts
                      renderTrigger={triggerContent => (
                        <TooltipTrigger asChild>
                          <EntityHeaderSubtitleButton>
                            {triggerContent}
                          </EntityHeaderSubtitleButton>
                        </TooltipTrigger>
                      )}
                    />
                    <TooltipContent side="bottom" align="start">
                      Click to select different resource type
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            />
          }
          backgroundImageContent={
            <EntityHeaderBackgroundImage
              // Blurred anyway: a small copy loads fast instead of the original file.
              imageUrl={maybe.mapOrMake(
                resource.profile_picture,
                profilePicture =>
                  rd.of(
                    convertImageFileWithCloudinary(
                      profilePicture,
                      IMAGE_QUALITY_OPTIONS.small
                    )
                  ),
                rd.ofIdle
              )}
            />
          }
          profileImageContent={
            <DropdownMenu>
              <SimpleTooltip
                align="start"
                title="Click to view options"
                side="bottom"
                open={!menuDisabled ? undefined : false}
              >
                <DropdownMenuTrigger asChild disabled={menuDisabled}>
                  <EntityHeaderImage
                    imageUrl={rd.of(
                      maybe.mapOrNull(
                        resource.profile_picture,
                        profilePicture =>
                          getAvatarImageURL(profilePicture, 'sm')
                      )
                    )}
                    fallbackContent={
                      !isResourcePaneV6
                        ? parseInitials(resource.first_name, resource.last_name)
                        : parseInitials(
                            resource.first_name?.trim(),
                            resource.last_name?.trim()
                          ) ||
                          resource.last_name?.trim().slice(0, 2) || (
                            <ResourceAvatarSilhouette />
                          )
                    }
                  />
                </DropdownMenuTrigger>
              </SimpleTooltip>
              <DropdownMenuContent align="start" side="bottom">
                {resource.profile_picture && (
                  <DropdownMenuItem onSelect={() => setImageOpen(true)}>
                    View in full screen
                  </DropdownMenuItem>
                )}

                {canUpdate && (
                  <DropdownMenuItem onSelect={triggerFileInput}>
                    Upload {resource.profile_picture ? 'new ' : ''}image
                  </DropdownMenuItem>
                )}
                {canUpdate && resource.profile_picture && (
                  <DropdownMenuItem
                    onSelect={() => {
                      handleChange('profile_picture', null);
                    }}
                  >
                    Remove image
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          }
          actionsContent={
            <EntityHeaderActions
              actions={rd.of(
                <>
                  {!hideOptions && (
                    // Hangs left into the empty space: at the contact page's
                    // 350px the full pill would push the header past the pane.
                    <span className="-ml-10 shrink-0 self-center">
                      <ResourceEditorVersionSwitch services={myServices} />
                    </span>
                  )}
                  {canUpdate && canUpdateBookings && (
                    <SimpleTooltip title="Add availability / booking">
                      <Button
                        variant="ghost"
                        layout="icon"
                        size="sm"
                        aria-label="Add availability / booking"
                        onClick={() => {
                          dispatch(
                            openModal(
                              'ResourceEventsModal',
                              { resourceId: resource.id },
                              slugAppScope
                            )
                          );
                        }}
                      >
                        <Stamp />
                      </Button>
                    </SimpleTooltip>
                  )}
                  {canCreateAvailability && (
                    <SimpleTooltip title="Request availability">
                      <Button
                        variant="ghost"
                        layout="icon"
                        size="sm"
                        aria-label="Request availability"
                        onClick={() => {
                          myServices.userFlowService.openRequestAvailabilityDialog(
                            {
                              resourceIds: [resource.id],
                              appScope: slugAppScope
                            }
                          );
                        }}
                      >
                        <CalendarSearch />
                      </Button>
                    </SimpleTooltip>
                  )}
                  {!hideFullLink && (
                    <SimpleTooltip title="View Profile">
                      <div className="">
                        <Button variant="ghost" layout="icon" size="sm" asChild>
                          <Link to={profilePath}>
                            <ArrowUpRight />
                          </Link>
                        </Button>
                      </div>
                    </SimpleTooltip>
                  )}
                  <ResourceOptions
                    buttonVariant="ghost"
                    resourceId={resource.id}
                    excludeOptions={[
                      // @ts-expect-error migrate to ts
                      'request-availability',
                      // @ts-expect-error migrate to ts
                      'add-availability'
                    ]}
                  />
                </>
              )}
            />
          }
          extraContent={
            // The edit frame hangs below the header (`-m-2`); paint it above
            // the v6 details' sticky section tabs (z-20), whose top bleed
            // would otherwise cut it off.
            <div className="relative z-30">
              {/* Groups may render from unvalidated data; a crash stays here. */}
              <EntityListRowErrorBoundary
                labels={{
                  title: tRow('renderCrashed'),
                  tryAgain: tRow('renderTryAgain')
                }}
              >
                <ResourceGroupsEditor
                  resourceId={resource.id}
                  readOnly={!canUpdate}
                  services={myServices}
                />
              </EntityListRowErrorBoundary>
              {notice}
            </div>
          }
        />
      </div>
      {resource.profile_picture && (
        <SingleImageLightbox
          src={convertImageFileWithCloudinary(
            resource.profile_picture,
            IMAGE_QUALITY_OPTIONS.large
          )}
          name={renderName(resource).trim() || 'Profile picture'}
          open={imageOpen}
          onOpenChange={setImageOpen}
        />
      )}
    </>
  );
};

ResourcePaneHeader.props = {
  resource: PropTypes.object,
  hideTabs: PropTypes.bool,
  hideOptions: PropTypes.bool,
  hideFullLink: PropTypes.bool
};

export default ResourcePaneHeader;
